import React, { useEffect, useState } from 'react';
import { Button, LinkText, Table } from '@adc/parallax-component-library';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../../table/DynamicTable';
import { Site, Subject } from '../../../models';
import { EntityStatus } from 'adc-sirius-module-common';
import i18n from '../../../utilities/i18n';
import { enumToCheckboxOptions } from '../../../utilities/helpers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSubjectMutateOptions,
  subjectsQueryOptions,
} from '../../../queryOptions';
import CreateSubjectModal from '../../modal/CreateSubjectModal';

type SubjectsTableProps = {
  siteKey: string;
  sites: Site[];
  navigateToSubject: (subjectKey: string, subjectLabel: string) => void;
  canCreateSubject?: boolean;
};

export const SubjectsTable = ({
  siteKey,
  sites,
  navigateToSubject,
  canCreateSubject = false,
}: SubjectsTableProps) => {
  const queryClient = useQueryClient();

  const subjects = useQuery(subjectsQueryOptions({ siteKey: siteKey }));

  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [subjectStatusFilters, setSubjectStatusFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  useEffect(() => {
    let filteredSubjects = subjects.data || [];

    subjectStatusFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'status') {
        filteredSubjects = filteredSubjects.filter((subject) =>
          activeFilterValues.includes(String(subject.status)),
        );
      }
    });

    setFilteredSubjects(filteredSubjects);
  }, [subjectStatusFilters, subjects.data]);

  const createSubject = useMutation(
    createSubjectMutateOptions({
      onSuccess: (subject) => {
        queryClient.setQueryData(
          subjectsQueryOptions({ siteKey }).queryKey,
          (subjects: Subject[] | undefined) => [...(subjects ?? []), subject],
        );
      },
    }),
  );

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const subjectsTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (subject: Subject) => getStatusLabel(subject.status),
    },
  ];

  const subjectsTableCellRenderer = (dataKey: string, subject: Subject) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSubject(subject.key, subject.name)}
            type="$bodySmall.default"
          >
            {subject[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(subject.status)}
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{subject[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <DynamicTable
      isLoading={subjects.isPending}
      hasLoadingError={subjects.isError}
      title={i18n.t('Page_Site.table_subjects_title')}
      description={i18n.t('Page_Site.table_subjects_description')}
      columnData={subjectsTableColumnData}
      rowData={filteredSubjects}
      cellRenderer={subjectsTableCellRenderer}
      enableFiltering={true}
      checkboxFilters={subjectStatusFilters}
      onFiltersChange={setSubjectStatusFilters}
      onFiltersReset={() => {
        setSubjectStatusFilters((prevFilters) =>
          prevFilters.map((filter) => ({
            ...filter,
            items: filter.items.map((item) => ({
              ...item,
              value: false,
            })),
          })),
        );
      }}
      toolbarRightContent={
        canCreateSubject ? (
          <CreateSubjectModal
            sites={sites}
            trigger={
              <Button
                size="medium"
                label={i18n.t('Page_Site.add_subject')}
                onPress={() => {}}
                disabled={createSubject.isPending}
                testID="create-subject-button"
              />
            }
            onSuccess={async (newSubject: Subject) => {
              await createSubject.mutateAsync({ subject: newSubject });
            }}
          />
        ) : null
      }
      testID="subjects-table"
    />
  );
};
